import { useCallback, useEffect, useRef, useState } from "react"
import {
  Animated,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
  ViewStyle,
} from "react-native"

import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import { useIsFocused } from "@react-navigation/native"
import { useEvent } from "expo"
import { Image } from "expo-image"
import * as NavigationBar from "expo-navigation-bar"
import { useFocusEffect, useLocalSearchParams } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { StatusBar } from "expo-status-bar"
import { useVideoPlayer, VideoView } from "expo-video"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import { HlsVideoView } from "src/components/HlsVideoView"
import { SidekickView } from "src/components/SidekickView"
import { useMediaAvatar } from "src/context/MediaAvatarContext"
import { useMediaContext } from "src/context/MediaContextProvider"
import { useMicrophone } from "src/context/MicrophoneContext"
import { useVisemes } from "src/context/VisemeContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useStatusBarCompensation } from "src/hooks/useStatusBarCompensation"
import { useWebMouseActivity } from "src/hooks/useWebMouseActivity"
import { backToRoot } from "src/utils/routerHelpers"
import { formatSecondsToTimestamp } from "src/utils/time"

export default function VideoPlayerScreen() {
  const { videoUrl, startTime, resourceId, title, taskId, totalLength, thumbnailUrl } =
    useLocalSearchParams<{
      videoUrl?: string | string[]
      startTime?: string
      resourceId?: string
      title?: string
      taskId?: string
      totalLength?: string
      thumbnailUrl?: string | string[]
    }>()
  const resolvedVideoUrl = Array.isArray(videoUrl) ? videoUrl[0] : videoUrl
  const resolvedThumbnailUrl = Array.isArray(thumbnailUrl) ? thumbnailUrl[0] : thumbnailUrl
  const resolvedStartTime = startTime ? parseFloat(startTime) : 0
  const { theme, navTheme } = useAppTheme()
  const insets = useSafeAreaInsets()
  const { effectiveTopInset } = useStatusBarCompensation()
  const { width, height } = useWindowDimensions()
  const isLandscape = width > height
  // True only while this screen is the focused/top route. Used to gate autoplay
  // and to pause if the screen is backgrounded (e.g. another player is pushed
  // on top), so a hidden player never keeps playing audio.
  const isFocused = useIsFocused()

  const videoRef = useRef<VideoView>(null)
  const [position, setPosition] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isScrubbing, setIsScrubbing] = useState(false)
  const [, setScrubTime] = useState<number | null>(null)
  const [isClosing, setIsClosing] = useState(false)
  const [isWebFullscreen, setIsWebFullscreen] = useState(false)
  // Show the thumbnail_url poster over the video until it actually starts
  // playing, so the user never sees the raw first frame while it loads.
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false)
  const pendingSeekRef = useRef<number | null>(null)

  const toggleWebFullscreen = useCallback(() => setIsWebFullscreen((v) => !v), [])

  const source = resolvedVideoUrl && resolvedVideoUrl.length > 0 ? resolvedVideoUrl : ""

  const player = useVideoPlayer(source, (p) => {
    p.loop = false
    p.timeUpdateEventInterval = 0.1
  })

  const { isListening } = useMicrophone()
  const { stopSpeech, isSpeaking } = useVisemes()
  const { setMediaContext, updateOffset, updateTotalLength, clearMediaContext } = useMediaContext()
  const { onMediaPlay, onMediaPause } = useMediaAvatar()

  const hasAutoplayed = useRef(false)
  const controlsOpacity = useRef(new Animated.Value(1)).current
  const overlayOpacity = useRef(new Animated.Value(0)).current
  const [isOverlayVisible, setIsOverlayVisible] = useState(false)
  const [overlayIcon, setOverlayIcon] = useState<"play-circle-filled" | "pause-circle">(
    "play-circle-filled",
  )
  const currentAnimationToken = useRef<object | null>(null)

  const { isPlaying } = useEvent(player, "playingChange", { isPlaying: player.playing })

  // Once playback has begun, permanently dismiss the thumbnail poster.
  useEffect(() => {
    if (isPlaying) {
      setHasStartedPlaying(true)
    }
  }, [isPlaying])

  const { isMouseActive, handleMouseMove } = useWebMouseActivity()
  const [isTyping, setIsTyping] = useState(false)
  // On web: show controls while mouse/keyboard active, user is typing, or paused.
  // isTyping prevents controls from fading while the text input is open, which
  // would otherwise trigger SidekickView's returnToIdle and collapse the input.
  // On native: show only when paused (unchanged).
  const shouldShowControls = !isPlaying || (Platform.OS === "web" && (isMouseActive || isTyping))

  // Unlock orientation for video player
  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.unlockAsync().catch(() => {})
      // Don't re-lock on cleanup to prevent jitter when auto-navigating between media
    }, []),
  )

  // Register media context for context-aware chat responses
  useEffect(() => {
    if (resourceId) {
      setMediaContext({
        resourceId,
        totalLength,
        taskId,
        title,
        initialOffset: formatSecondsToTimestamp(resolvedStartTime),
      })
    }
    return () => {
      clearMediaContext()
    }
  }, [
    resourceId,
    totalLength,
    taskId,
    title,
    resolvedStartTime,
    setMediaContext,
    clearMediaContext,
  ])

  // Update media context offset during playback
  useEffect(() => {
    if (resourceId && !isScrubbing) {
      updateOffset(formatSecondsToTimestamp(position))
    }
  }, [resourceId, position, isScrubbing, updateOffset])

  // Update media context total length when video duration is loaded
  useEffect(() => {
    if (resourceId && duration > 0) {
      const formattedDuration = formatSecondsToTimestamp(duration)
      updateTotalLength(formattedDuration)
    }
  }, [resourceId, duration, updateTotalLength])

  // Handle Android navigation bar
  useEffect(() => {
    if (Platform.OS === "android") {
      NavigationBar.setVisibilityAsync("hidden")
      NavigationBar.setBehaviorAsync("overlay-swipe")
      NavigationBar.setBackgroundColorAsync("#000000")
      NavigationBar.setButtonStyleAsync("light")
    }

    return () => {
      if (Platform.OS === "android") {
        NavigationBar.setVisibilityAsync("visible")
        NavigationBar.setBackgroundColorAsync(theme.colors.background)
        NavigationBar.setButtonStyleAsync(navTheme.dark ? "light" : "dark")
      }
    }
  }, [theme.colors.background, navTheme.dark])

  // Keep a ref so the effect below can read isSpeaking without adding it as a
  // dep (which would fire the effect when speaking starts and wrongly call stopSpeech).
  const isSpeakingRef = useRef(isSpeaking)
  isSpeakingRef.current = isSpeaking

  // Hide/show avatar based on playing state.
  // When the video is paused automatically because the sidekick is speaking,
  // skip onMediaPause so the avatar is driven entirely by autoShowAvatarOnSpeech
  // rather than fighting it — the conflict was causing the Rive animation to reset.
  // Guard stopSpeech behind !isSpeakingRef: expo-video can fire a brief spurious
  // "playing" event right after player.pause() (similar to the YouTube IFrame race),
  // which would clear the audio queue and silence the sidekick mid-speech.
  useEffect(() => {
    const handlePlayPause = async () => {
      if (isPlaying) {
        if (!isSpeakingRef.current) {
          await stopSpeech()
        }
        onMediaPlay() // Hide avatar when video plays
      } else if (!isSpeakingRef.current) {
        onMediaPause() // Restore avatar when video pauses (user-driven or listening)
      }
    }
    handlePlayPause()
  }, [isPlaying, stopSpeech, onMediaPlay, onMediaPause])

  // Animate controls opacity: fade out when hidden, fade in when visible.
  // On web this reacts to both playback state and mouse activity.
  useEffect(() => {
    Animated.timing(controlsOpacity, {
      toValue: shouldShowControls ? 1 : 0,
      duration: 300,
      useNativeDriver: true,
    }).start()
  }, [shouldShowControls, controlsOpacity])

  // Pause video when listening (Talk mode) or when the sidekick starts speaking
  // so its TTS reply is audible. On Android, the playing video holds exclusive
  // audio focus (DoNotMix), so we must pause before the sidekick can be heard.
  useEffect(() => {
    if (isListening || isSpeaking) {
      player.pause()
    }
  }, [isListening, isSpeaking, player])

  const { status: playerStatus } = useEvent(player, "statusChange", { status: player.status })
  const { duration: loadedDuration, availableVideoTracks } = useEvent(player, "sourceLoad", {
    videoSource: null,
    duration: player.duration ?? 0,
    availableVideoTracks: [],
    availableSubtitleTracks: [],
    availableAudioTracks: [],
  })

  const videoTrack = availableVideoTracks?.[0]
  const videoWidth = videoTrack?.size?.width ?? 0
  const videoHeight = videoTrack?.size?.height ?? 0
  const isVideoPortrait = videoHeight > videoWidth && videoWidth > 0

  const { currentTime } = useEvent(player, "timeUpdate", {
    currentTime: player.currentTime ?? 0,
    currentLiveTimestamp: null,
    currentOffsetFromLive: null,
    bufferedPosition: player.bufferedPosition ?? 0,
  })

  const syncDurationFromPlayer = useCallback(() => {
    const nativeDuration = player.duration ?? 0
    if (nativeDuration > 0 && Number.isFinite(nativeDuration)) {
      setDuration((current) => {
        return Math.abs(current - nativeDuration) > 0.1 ? nativeDuration : current
      })
    }
  }, [player])

  useEffect(() => {
    if (loadedDuration && Number.isFinite(loadedDuration) && loadedDuration > 0) {
      setDuration((current) => {
        return Math.abs(current - loadedDuration) > 0.1 ? loadedDuration : current
      })
      return
    }

    if (playerStatus === "readyToPlay") {
      syncDurationFromPlayer()
    }
  }, [loadedDuration, playerStatus, syncDurationFromPlayer])

  // Sync duration from player when we have time updates but duration is still 0 (needed for web/Android where sourceLoad may not provide duration)
  useEffect(() => {
    if (duration > 0) {
      return
    }

    syncDurationFromPlayer()
  }, [currentTime, duration, syncDurationFromPlayer])

  useEffect(() => {
    if (!isScrubbing && typeof currentTime === "number" && !Number.isNaN(currentTime)) {
      // If we have a pending seek, check if the player has caught up to it
      if (pendingSeekRef.current !== null) {
        // Consider the seek complete if the player is within 0.5s of the target
        if (Math.abs(currentTime - pendingSeekRef.current) < 0.5) {
          pendingSeekRef.current = null
        }
      }
      // Only update position from player if we don't have a pending seek
      if (pendingSeekRef.current === null) {
        setPosition(currentTime)
      }
    }
  }, [currentTime, isScrubbing])

  // Autoplay once the player is ready and the sidekick has finished speaking.
  // Do not autoplay while the user is listening (Talk) to avoid the mic capturing playback.
  // Seek here (not in the useVideoPlayer setup callback) because the setup runs before the
  // source is loaded — the native player resets currentTime to 0 once loading completes.
  useEffect(() => {
    if (playerStatus !== "readyToPlay" || isSpeaking || isListening || hasAutoplayed.current) return
    // Don't autoplay unless this screen is actually on top. If several media
    // events arrived together (or another player was pushed over this one),
    // this instance can be mounted but hidden — autoplaying here would produce
    // audible playback from a video that isn't visible.
    if (!isFocused) return
    hasAutoplayed.current = true
    if (resolvedStartTime > 0) {
      pendingSeekRef.current = resolvedStartTime
      player.currentTime = resolvedStartTime
    }
    player.play()
  }, [playerStatus, isSpeaking, isListening, isFocused, player, resolvedStartTime])

  // Pause whenever the screen loses focus (another screen pushed on top, app
  // backgrounded, etc.). expo-video's player keeps playing independently of the
  // VideoView, so without this a hidden player would continue emitting audio.
  useEffect(() => {
    if (!isFocused) {
      player.pause()
    }
  }, [isFocused, player])

  // Belt-and-suspenders: guarantee playback stops if this screen unmounts (e.g.
  // popped back to chat). On iOS expo-video can keep emitting audio briefly if
  // the player isn't explicitly paused before it's released — this is the
  // "video playing in the background that I couldn't stop" symptom. The cleanup
  // only runs on unmount / player swap, so it never fights autoplay.
  useEffect(() => {
    return () => {
      try {
        player.pause()
      } catch {
        // Player may already be released; ignore.
      }
    }
  }, [player])

  const handleContentPress = useCallback(() => {
    // Toggle play/pause on tap
    const willPlay = !isPlaying
    if (isPlaying) {
      player.pause()
    } else {
      player.play()
    }
    // Create unique token for this animation FIRST to invalidate any in-progress animation
    const animationToken = {}
    currentAnimationToken.current = animationToken
    // Stop any in-progress animation cleanly
    overlayOpacity.stopAnimation(() => {
      // After animation stops, update icon and restart
      setOverlayIcon(willPlay ? "play-circle-filled" : "pause-circle")
      overlayOpacity.setValue(0.75)
      setIsOverlayVisible(true)
      // Start new animation
      Animated.timing(overlayOpacity, {
        toValue: 0,
        duration: 800,
        delay: 1500,
        useNativeDriver: true,
      }).start(({ finished }) => {
        // Only hide if this is still the current animation
        if (finished && currentAnimationToken.current === animationToken) {
          setIsOverlayVisible(false)
        }
      })
    })
  }, [isPlaying, player, overlayOpacity])

  const togglePause = useCallback(() => {
    const willPlay = !isPlaying
    if (isPlaying) {
      player.pause()
    } else {
      player.play()
    }
    // Create unique token for this animation FIRST to invalidate any in-progress animation
    const animationToken = {}
    currentAnimationToken.current = animationToken
    // Stop any in-progress animation cleanly
    overlayOpacity.stopAnimation(() => {
      // After animation stops, update icon and restart
      setOverlayIcon(willPlay ? "play-circle-filled" : "pause-circle")
      overlayOpacity.setValue(0.75)
      setIsOverlayVisible(true)
      // Start new animation
      Animated.timing(overlayOpacity, {
        toValue: 0,
        duration: 800,
        delay: 1500,
        useNativeDriver: true,
      }).start(({ finished }) => {
        // Only hide if this is still the current animation
        if (finished && currentAnimationToken.current === animationToken) {
          setIsOverlayVisible(false)
        }
      })
    })
  }, [isPlaying, player, overlayOpacity])

  // Handle mode changes from SidekickView
  const handleModeChange = useCallback(
    (mode: "idle" | "talk" | "type") => {
      setIsTyping(mode === "type")
      if (mode === "talk" || mode === "type") {
        // Pause immediately so the video releases audio focus before the
        // sidekick's TTS chunks arrive. On Android (expo-video uses DoNotMix
        // by default) the playing video holds exclusive audio focus; if we
        // wait until isSpeaking=true to pause, expo-av races to play while
        // the video still owns audio focus and the speech plays silently.
        player.pause()
      }
    },
    [player],
  )

  if (!resolvedVideoUrl) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Unable to load video.</Text>
      </View>
    )
  }

  return (
    <View style={styles.container} {...({ onMouseMove: handleMouseMove } as any)}>
      <StatusBar style="light" hidden={isPlaying && isLandscape} />

      {/* Close button - above SidekickView */}
      <Animated.View
        style={[
          styles.closeButton,
          {
            top: effectiveTopInset + 22,
            right: insets.right + 12,
          },
          Platform.OS === "web" && ({ position: "fixed" } as any),
        ]}
        pointerEvents="auto"
      >
        <TouchableOpacity
          onPress={async () => {
            // Unmount AvatarOverlay if in landscape to prevent crash during orientation change (APP-738)
            if (isLandscape) {
              setIsClosing(true)
              // Wait for React to unmount the component
              await new Promise((resolve) => setTimeout(resolve, 0))
            }
            backToRoot()
          }}
          style={[styles.closeButtonInner, { backgroundColor: theme.colors.buttonBackground }]}
          accessibilityRole="button"
          accessibilityLabel="Close video player"
        >
          <MaterialIcons name="close" size={20} color={theme.colors.buttonText} />
        </TouchableOpacity>
      </Animated.View>

      {!isClosing && (
        <SidekickView
          showAvatar
          autoShowAvatarOnSpeech
          showCaptions
          showCCToggle
          showButtonCluster
          compactButtonCluster
          showControlsBarBackground={false}
          controlsOpacity={controlsOpacity}
          controlsVisible={shouldShowControls}
          onContentPress={handleContentPress}
          onModeChange={handleModeChange}
          riveOwner="video"
          controlsConfig={{
            type: "playback",
            isPlaying,
            onTogglePlay: togglePause,
            position,
            duration,
            isScrubbing,
            onScrubStart: () => {
              setIsScrubbing(true)
              setScrubTime(position)
            },
            onScrubChange: (value: number) => {
              setPosition(value)
              setScrubTime(value)
            },
            onScrubEnd: (value: number) => {
              setIsScrubbing(false)
              setScrubTime(null)
              // Keep the position at the scrubbed value to prevent jumping
              setPosition(value)
              pendingSeekRef.current = value
              player.currentTime = value
            },
            ...(Platform.OS === "web" && {
              isFullscreen: isWebFullscreen,
              onToggleFullscreen: toggleWebFullscreen,
            }),
          }}
        >
          {/* Video content. HlsVideoView is the same as expo-video's VideoView
              on native, but on web it routes HLS sources through hls.js so
              Chrome/Firefox can seek directly to the requested segment instead
              of streaming the whole playlist linearly. */}
          <HlsVideoView
            ref={videoRef}
            style={[
              StyleSheet.absoluteFill,
              Platform.OS === "web" &&
                ({
                  width: "100%",
                  height: "100%",
                } as unknown as ViewStyle),
            ]}
            contentFit={isVideoPortrait && !isLandscape ? "cover" : "contain"}
            nativeControls={false}
            player={player}
            allowsFullscreen
            allowsPictureInPicture
            pointerEvents="none"
            {...(Platform.OS === "web" && { crossOrigin: "anonymous" as const })}
          />

          {/* Thumbnail poster shown over the video until playback begins, so
              the user sees thumbnail_url instead of the raw first frame. */}
          {!!resolvedThumbnailUrl && !hasStartedPlaying && (
            <Image
              source={{ uri: resolvedThumbnailUrl }}
              style={StyleSheet.absoluteFill}
              contentFit={isVideoPortrait && !isLandscape ? "cover" : "contain"}
              pointerEvents="none"
            />
          )}
        </SidekickView>
      )}

      {/* Play/Pause overlay animation */}
      {isOverlayVisible && (
        <Animated.View
          pointerEvents="none"
          style={[styles.fullscreenOverlay, { opacity: overlayOpacity }]}
        >
          <MaterialIcons name={overlayIcon} size={120} color="#FFFFFF" accessibilityRole="image" />
        </Animated.View>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#000000",
  },
  errorContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    backgroundColor: "#000000",
  },
  errorText: {
    color: "#FFFFFF",
    textAlign: "center",
  },
  closeButton: {
    position: "absolute",
    zIndex: 3200,
  },
  closeButtonInner: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: "center",
    alignItems: "center",
  },
  fullscreenOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.3)",
    zIndex: 2050,
  },
})
